/**
 * Pure, framework-free composition of the META "Réserver" deep link (tasks/meta-reserve.md).
 *
 * The MVP is the bio-link fallback blessed by the spec: it REUSES the standalone page that
 * `booking-widget-embed` ships (`book.<domain>/v/<slug>`) and simply carries `channel=meta`, so a
 * booking that arrives from a venue's Instagram/Facebook profile is attributed to the canonical `META`
 * `BookingChannel` (DOMAIN.md §B.6) — one analytics-grade tag that splits social traffic from generic
 * embed traffic. There is NO Meta SDK here and NO new server state: "connecting" is the manager pasting
 * the link into their profile, "disconnecting" is removing it (spec Tech decisions §Persistence).
 *
 * The native in-profile Reserve/Book Now button is DEFERRED behind an integration adapter
 * (`MetaListingPort` in `integrations/meta`) until Meta business approval + wire-format verification —
 * NOT shipped here, mirroring `rwg-business-link`'s readiness posture.
 *
 * Everything is a string transform so the generator UI stays trivially unit-testable and the link is
 * composed ENTIRELY client-side.
 */

import { resolveWidgetOrigin } from "../embed/snippet";

export { resolveWidgetOrigin };

/** The canonical query tag the standalone page forwards to the create endpoint as `BookingChannel.META`. */
export const META_CHANNEL = "meta";

/**
 * The two Meta surfaces, offered INDEPENDENTLY (spec Open question 2 — a venue may be present on only
 * one). Both links are byte-identical (they carry the SAME canonical `channel=meta` tag — the spec
 * forbids a locally-forked social sub-channel set); the split is purely a placement affordance so the
 * manager drops the link into each profile they actually have.
 */
export const META_PLATFORMS = ["instagram", "facebook"] as const;
export type MetaPlatform = (typeof META_PLATFORMS)[number];

/**
 * Optional default-language override baked into the link as `?lang=` — the standalone page reads it
 * (localization-i18n R1). `auto` means "omit it": the page resolves the diner's language against the
 * venue default.
 */
export const META_LANGS = ["auto", "fr", "en"] as const;
export type MetaLang = (typeof META_LANGS)[number];

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

/**
 * The META "Réserver" deep link `https://book.<domain>/v/<slug>?channel=meta` (+ optional `&lang=`).
 * `channel` comes first for a stable, testable ordering; the slug is a `[a-z0-9-]` value (DOMAIN.md
 * §B.6) but is still percent-encoded defensively.
 */
export function buildMetaReserveUrl(options: {
  origin: string;
  slug: string;
  lang?: MetaLang;
}): string {
  const base = `${stripTrailingSlash(options.origin)}/v/${encodeURIComponent(options.slug)}`;
  const params = [`channel=${META_CHANNEL}`];
  if (options.lang && options.lang !== "auto") params.push(`lang=${options.lang}`);
  return `${base}?${params.join("&")}`;
}
